import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Share2, Copy, Check, Edit2, Cloud, CloudOff, RefreshCw } from 'lucide-react'
import { useCricketStore } from '#/store/cricketStore'
import {
  formatOvers,
  calculateRunRate,
  calculateStrikeRate,
  calculateEconomyRate,
} from '#/lib/utils'
import { Checkbox } from '#/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select'
import { AutocompleteSelect } from '#/components/ui/AutocompleteSelect'
import { getMatchFn } from '#/server/match.functions'
import type { ExtraType, MatchState } from '#/types/cricket'

export const Route = createFileRoute('/match/$matchId')({
  validateSearch: (search: Record<string, unknown>): { mode?: 'edit' | 'view'; token?: string } => {
    return {
      mode: (search.mode as 'edit' | 'view') || 'edit',
      token: (search.token as string) || '',
    }
  },
  loader: async ({ params }) => {
    try {
      const res = await getMatchFn({ data: { id: params.matchId } })
      return { serverMatch: res.match || null }
    } catch {
      return { serverMatch: null }
    }
  },
  component: LiveMatchConsole,
})

function LiveMatchConsole() {
  const { matchId } = Route.useParams()
  const search = useSearch({ from: '/match/$matchId' })
  const loaderData = Route.useLoaderData()
  const navigate = useNavigate()

  const currentMatch = useCricketStore((state) => state.currentMatch)
  const savedMatches = useCricketStore((state) => state.savedMatches)
  const createdMatchIds = useCricketStore((state) => state.createdMatchIds || [])
  const savedPlayerNames = useCricketStore((state) => state.savedPlayerNames)
  const teamsList = useCricketStore((state) => state.teamsList)
  const syncMatchState = useCricketStore((state) => state.syncMatchState)
  const recordDelivery = useCricketStore((state) => state.recordDelivery)
  const undoLastDelivery = useCricketStore((state) => state.undoLastDelivery)
  const swapBatsmen = useCricketStore((state) => state.swapBatsmen)
  const changeBowler = useCricketStore((state) => state.changeBowler)
  const retireBatsman = useCricketStore((state) => state.retireBatsman)
  const updateMatchSettingsFirstInnings = useCricketStore((state) => state.updateMatchSettingsFirstInnings)
  const syncOnline = useCricketStore((state) => state.syncOnline)

  const match =
    currentMatch?.id === matchId
      ? currentMatch
      : savedMatches.find((m) => m.id === matchId) || loaderData?.serverMatch || null

  // A user is view-only if mode is 'view', or if they do not own this match and don't have the edit token
  const isMatchOwner = createdMatchIds.includes(matchId)
  const isViewOnly =
    search.mode === 'view' ||
    (!isMatchOwner && (!search.token || (match && search.token !== match.tokens.editToken)))

  useEffect(() => {
    if (!currentMatch || currentMatch.id !== matchId) {
      if (loaderData?.serverMatch) {
        syncMatchState(loaderData.serverMatch)
      } else {
        const found = savedMatches.find((m) => m.id === matchId)
        if (found) syncMatchState(found)
      }
    }
  }, [matchId, currentMatch, savedMatches, loaderData, syncMatchState])

  // Automatic real-time score sync for viewers without requiring page refresh
  // Trigger 4 & 6 animation ONLY on viewer screens when new boundary delivery arrives
  const prevDeliveryCountRef = useState<{ balls: number; runs: number }>({
    balls: match?.legalBallsDelivered ?? 0,
    runs: match?.totalRuns ?? 0,
  })[0]

  useEffect(() => {
    if (!isViewOnly) return
    const interval = setInterval(async () => {
      try {
        const res = await getMatchFn({ data: { id: matchId } })
        if (res.match) {
          const newMatch = res.match
          const lastDelivery = newMatch.currentOverDeliveries?.[newMatch.currentOverDeliveries.length - 1]
          if (
            lastDelivery &&
            (newMatch.legalBallsDelivered !== prevDeliveryCountRef.balls ||
              newMatch.totalRuns !== prevDeliveryCountRef.runs)
          ) {
            prevDeliveryCountRef.balls = newMatch.legalBallsDelivered
            prevDeliveryCountRef.runs = newMatch.totalRuns

            if (lastDelivery.runsScored === 4 && (lastDelivery.extraType === 'none' || lastDelivery.extraType === 'no_ball')) {
              setBoundaryAnimation(4)
              setTimeout(() => setBoundaryAnimation(null), 1400)
            } else if (lastDelivery.runsScored === 6 && (lastDelivery.extraType === 'none' || lastDelivery.extraType === 'no_ball')) {
              setBoundaryAnimation(6)
              setTimeout(() => setBoundaryAnimation(null), 1400)
            }
          }
          syncMatchState(newMatch)
        }
      } catch {
        // silence background poll errors
      }
    }, 1500)
    return () => clearInterval(interval)
  }, [isViewOnly, matchId, syncMatchState])

  // Scoring controls state
  const [selectedExtra, setSelectedExtra] = useState<ExtraType>('none')
  const [isWicketChecked, setIsWicketChecked] = useState(false)

  // Modals & Sheets
  const [showShareModal, setShowShareModal] = useState(false)
  const [showBowlerModal, setShowBowlerModal] = useState(false)
  const [nextBowlerName, setNextBowlerName] = useState('')
  const [bowlerSelectError, setBowlerSelectError] = useState('')
  const [showPartnershipsSheet, setShowPartnershipsSheet] = useState(false)
  const [showExtrasSheet, setShowExtrasSheet] = useState(false)
  const [showCustomRunsModal, setShowCustomRunsModal] = useState(false)
  const [customRunsInput, setCustomRunsInput] = useState(0)
  const [showWicketModal, setShowWicketModal] = useState(false)
  const [newBatsmanInput, setNewBatsmanInput] = useState('')
  const [wicketTypeSelected, setWicketTypeSelected] = useState<'bowled' | 'caught' | 'run_out' | 'lbw'>('bowled')
  const [dismissedBatsman, setDismissedBatsman] = useState<string>('')
  const [copiedLink, setCopiedLink] = useState<'view' | 'edit' | null>(null)
  const [copiedMatchId, setCopiedMatchId] = useState(false)

  const displayMatchId = match?.id?.replace(/^match_/, '') || ''

  const copyMatchId = async () => {
    if (!displayMatchId) return
    try {
      await navigator.clipboard.writeText(displayMatchId)
      setCopiedMatchId(true)
      setTimeout(() => setCopiedMatchId(false), 2000)
    } catch {
      // fallback
    }
  }

  // Screen 13: Retire Player Modal state
  const [showRetireModal, setShowRetireModal] = useState(false)
  const [playerToRetire, setPlayerToRetire] = useState<string>('')
  const [replacementName, setReplacementName] = useState<string>('')

  // First Inning Edit Overs & Players Modal state
  const [showEditSettingsModal, setShowEditSettingsModal] = useState(false)
  const [editOversInput, setEditOversInput] = useState<number | string>('')
  const [editPlayersInput, setEditPlayersInput] = useState<number | string>('')
  const [editError, setEditError] = useState<string>('')

  // Screen 14: End of first innings modal state
  const [showEndOfInningsModal, setShowEndOfInningsModal] = useState(false)
  const [endOfInningsData, setEndOfInningsData] = useState<{
    chasingTeam: string
    runsNeeded: number
    overs: number
    rrr: string
  } | null>(null)

  // Screen 20: Match Celebration state
  const [showCelebration, setShowCelebration] = useState(false)
  const [celebrationData, setCelebrationData] = useState<{
    winnerTeam: string
    winnerText: string
    isDraw?: boolean
  } | null>(null)

  // Online / Offline & Sync status
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleSyncToOnline = async () => {
    if (isSyncing) return
    setIsSyncing(true)
    setSyncStatusMsg(null)
    const success = await syncOnline()
    setIsSyncing(false)
    if (success) {
      setSyncStatusMsg('Synced online successfully!')
    } else {
      setSyncStatusMsg('Sync failed. Please check internet connection.')
    }
    setTimeout(() => setSyncStatusMsg(null), 3000)
  }

  // 3D Animated 4 & 6 Boundary Overlay state
  const [boundaryAnimation, setBoundaryAnimation] = useState<4 | 6 | null>(null)

  // Auto show victory celebration when match has completed (for spectator or scorer)
  useEffect(() => {
    if (match && (match.status === 'completed' || match.winnerText)) {
      const isTieOrDraw =
        match.winnerText?.toLowerCase().includes('tie') ||
        match.winnerText?.toLowerCase().includes('draw')
      setCelebrationData({
        winnerTeam: match.winnerTeam || match.battingTeam,
        winnerText: match.winnerText || `${match.winnerTeam || match.battingTeam} won the match!`,
        isDraw: Boolean(isTieOrDraw),
      })
      setShowCelebration(true)
    }
  }, [match?.status, match?.winnerText, match?.winnerTeam, match?.battingTeam])

  // Automatically enforce Bowler Selection popup whenever an over is pending a new bowler
  useEffect(() => {
    if (!isViewOnly && match?.overPendingBowler && match.status !== 'completed') {
      setShowBowlerModal(true)
    }
  }, [match?.overPendingBowler, match?.status, isViewOnly])

  if (!match) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-4">Match not found.</p>
        <button
          onClick={() => navigate({ to: '/' })}
          className="bg-[#2E7D32] text-white px-4 py-2 rounded-md font-medium"
        >
          Go to Home
        </button>
      </div>
    )
  }

  const titleText = `${match.hostTeam} v/s ${match.visitorTeam.slice(0, 1)}...`
  const crr = calculateRunRate(match.totalRuns, match.legalBallsDelivered)

  // Target and Required Run Rate for 2nd Inning (Screens 17 & 18)
  const isSecondInning = match.currentInnings === 2
  const target = match.targetRuns || 0
  const remainingRuns = Math.max(0, target - match.totalRuns)
  const totalInningBalls = match.totalOvers * 6
  const remainingBalls = Math.max(0, totalInningBalls - match.legalBallsDelivered)
  const remainingOvers = remainingBalls / 6
  const rrr = remainingOvers > 0 ? (remainingRuns / remainingOvers).toFixed(2) : '0.00'

  // Fixed batsman rows: order strictly by who came first (player 1 vs player 2)
  // so the rows don't violently jump when swapping strike on single runs!
  // The active striker is indicated by an asterisk (*) and bold theme highlight.
  const fallbackStriker = {
    name: match.striker,
    runs: 0,
    balls: 0,
    fours: 0,
    sixes: 0,
  }
  const fallbackNonStriker = {
    name: match.nonStriker,
    runs: 0,
    balls: 0,
    fours: 0,
    sixes: 0,
  }

  // Player 1 and Player 2 in initial partnership entry order
  const p1Name = match.currentPartnership.player1Name || match.striker
  const p2Name = match.currentPartnership.player2Name || match.nonStriker

  const batterRow1 = match.batsmen[p1Name] || fallbackStriker
  const batterRow2 = p2Name && p2Name !== p1Name ? (match.batsmen[p2Name] || fallbackNonStriker) : null

  const isRow1Striker = batterRow1.name === match.striker
  const isRow2Striker = batterRow2 ? batterRow2.name === match.striker : false
  const currentBowlerStats = match.bowlers[match.currentBowler] || {
    name: match.currentBowler,
    legalBalls: 0,
    maidens: 0,
    runsConceded: 0,
    wickets: 0,
  }

  const maxAllowedWickets = Math.max(1, match.settings.playersPerTeam - 1)
  const isFirstInningOver =
    match.currentInnings === 1 &&
    (match.legalBallsDelivered >= match.totalOvers * 6 || match.totalWickets >= maxAllowedWickets)
  const isMatchFinished = match.status === 'completed' || (match.currentInnings === 2 && isFirstInningOver)

  const handleScoreBall = (runs: number) => {
    if (isViewOnly) return

    if (isMatchFinished) {
      return
    }

    if (isFirstInningOver) {
      const runsNeeded = match.totalRuns + 1
      const reqRR = (runsNeeded / match.totalOvers).toFixed(2)
      setEndOfInningsData({
        chasingTeam: match.bowlingTeam,
        runsNeeded,
        overs: match.totalOvers,
        rrr: reqRR,
      })
      setShowEndOfInningsModal(true)
      return
    }

    if (match.overPendingBowler) {
      setNextBowlerName('')
      setBowlerSelectError('Please choose the new bowler for this over before scoring.')
      setShowBowlerModal(true)
      return
    }

    if (isWicketChecked) {
      setDismissedBatsman(match.striker)
      setShowWicketModal(true)
      return
    }

    const result = recordDelivery({
      runs,
      extraType: selectedExtra,
      isWicket: false,
    })

    // 4 & 6 Animated Scope Zoom: ONLY for viewers (view-mode spectators), NOT for the scorer/host
    if (isViewOnly) {
      if (runs === 4 && (selectedExtra === 'none' || selectedExtra === 'no_ball')) {
        setBoundaryAnimation(4)
        setTimeout(() => setBoundaryAnimation(null), 1400)
      } else if (runs === 6 && (selectedExtra === 'none' || selectedExtra === 'no_ball')) {
        setBoundaryAnimation(6)
        setTimeout(() => setBoundaryAnimation(null), 1400)
      }
    }

    setSelectedExtra('none')
    setIsWicketChecked(false)

    if (result.inningsCompleted && match.currentInnings === 1) {
      const runsNeeded = match.totalRuns + runs + 1
      const reqRR = (runsNeeded / match.totalOvers).toFixed(2)
      setEndOfInningsData({
        chasingTeam: match.bowlingTeam,
        runsNeeded,
        overs: match.totalOvers,
        rrr: reqRR,
      })
      setShowEndOfInningsModal(true)
      return
    }

    if (result.matchCompleted && match.currentInnings === 2) {
      const isTieOrDraw =
        result.winnerText?.toLowerCase().includes('tie') ||
        result.winnerText?.toLowerCase().includes('draw')
      setCelebrationData({
        winnerTeam: result.winnerTeam || match.battingTeam,
        winnerText: result.winnerText || `${match.battingTeam} won the match!`,
        isDraw: Boolean(isTieOrDraw),
      })
      setShowCelebration(true)
      return
    }

    if (result.overCompleted) {
      setNextBowlerName('')
      setBowlerSelectError('')
      setShowBowlerModal(true)
    }
  }

  const handleConfirmWicket = (runs: number) => {
    if (isMatchFinished || isFirstInningOver) {
      return
    }

    if (match.overPendingBowler) {
      setNextBowlerName('')
      setBowlerSelectError('Please choose the new bowler for this over before scoring.')
      setShowBowlerModal(true)
      return
    }

    const result = recordDelivery({
      runs,
      extraType: selectedExtra,
      isWicket: true,
      wicketType: wicketTypeSelected,
      dismissedPlayer: dismissedBatsman || match.striker,
      newBatsmanName: newBatsmanInput.trim() || `Player ${match.totalWickets + 3}`,
    })

    setShowWicketModal(false)
    setNewBatsmanInput('')
    setSelectedExtra('none')
    setIsWicketChecked(false)

    if (result.inningsCompleted && match.currentInnings === 1) {
      const runsNeeded = match.totalRuns + 1
      const reqRR = (runsNeeded / match.totalOvers).toFixed(2)
      setEndOfInningsData({
        chasingTeam: match.bowlingTeam,
        runsNeeded,
        overs: match.totalOvers,
        rrr: reqRR,
      })
      setShowEndOfInningsModal(true)
      return
    }

    if (result.matchCompleted && match.currentInnings === 2) {
      const isTieOrDraw =
        result.winnerText?.toLowerCase().includes('tie') ||
        result.winnerText?.toLowerCase().includes('draw')
      setCelebrationData({
        winnerTeam: result.winnerTeam || match.bowlingTeam,
        winnerText: result.winnerText || `${match.bowlingTeam} won the match!`,
        isDraw: Boolean(isTieOrDraw),
      })
      setShowCelebration(true)
      return
    }

    if (result.overCompleted) {
      setNextBowlerName('')
      setBowlerSelectError('')
      setShowBowlerModal(true)
    }
  }

  const handleBowlerChosen = (chosenName: string) => {
    const clean = chosenName.trim()
    if (!clean) {
      setBowlerSelectError('Please choose or enter a bowler name')
      return
    }
    if (clean.toLowerCase() === match.currentBowler.toLowerCase()) {
      setBowlerSelectError(`${clean} just bowled the previous over. The same bowler cannot bowl consecutive overs.`)
      return
    }
    setBowlerSelectError('')
    changeBowler(clean)
    setShowBowlerModal(false)
    setNextBowlerName('')
  }

  const handleConfirmRetire = (e: React.FormEvent) => {
    e.preventDefault()
    if (!playerToRetire || !replacementName.trim()) return
    retireBatsman(playerToRetire, replacementName.trim())
    setShowRetireModal(false)
    setReplacementName('')
    setPlayerToRetire('')
  }

  const copyToClipboard = async (type: 'view' | 'edit') => {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const token = type === 'view' ? match.tokens.viewToken : match.tokens.editToken
    const url = `${origin}/match/${match.id}?mode=${type}&token=${token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedLink(type)
      setTimeout(() => setCopiedLink(null), 2500)
    } catch {
      // fallback
    }
  }

  const shareToWhatsApp = (type: 'view' | 'edit') => {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const token = type === 'view' ? match.tokens.viewToken : match.tokens.editToken
    const url = `${origin}/match/${match.id}?mode=${type}&token=${token}`
    const matchTitle = `${match.hostTeam} vs ${match.visitorTeam}`
    const text =
      type === 'view'
        ? `🏏 Watch Live Cricket: ${matchTitle}\nScore: ${match.totalRuns}/${match.totalWickets} (${formatOvers(match.legalBallsDelivered)} ov)\nWatch live here: ${url}`
        : `🏏 Cricket Scorer Access: ${matchTitle}\nTake over scoring here: ${url}`
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`
    window.open(whatsappUrl, '_blank')
  }

  // Filter suggested bowlers for Screen 19 Choose Bowler autocomplete
  // In the 2nd innings, bowling team is the team that batted in 1st innings
  const bowlingTeamRecord = teamsList.find(
    (t) => t.name.toLowerCase() === match.bowlingTeam.toLowerCase()
  )
  const battingTeamRecord = teamsList.find(
    (t) => t.name.toLowerCase() === match.battingTeam.toLowerCase()
  )

  const firstInningsBatsmenNames =
    match.currentInnings === 2 ? Object.keys(match.innings1?.batsmen || {}) : []

  // Suggested bowlers: ONLY from the bowling team (exclude currently finished bowler)
  const bowlingTeamRoster = bowlingTeamRecord?.players?.length
    ? bowlingTeamRecord.players
    : (firstInningsBatsmenNames.length > 0 ? firstInningsBatsmenNames : [])

  const availableBowlerPool = bowlingTeamRoster.length > 0
    ? Array.from(new Set([...bowlingTeamRoster, ...firstInningsBatsmenNames]))
    : savedPlayerNames

  const suggestedBowlers = availableBowlerPool
    .filter((p) => p.toLowerCase() !== match.currentBowler.toLowerCase())
    .filter((p) => (nextBowlerName ? p.toLowerCase().includes(nextBowlerName.toLowerCase()) : true))
    .map((name) => {
      const stats = match.bowlers?.[name]
      const oversText = stats
        ? `Overs: ${formatOvers(stats.legalBalls)} (R: ${stats.runsConceded}, W: ${stats.wickets})`
        : 'Overs: 0'
      return {
        label: name,
        value: name,
        subtext: oversText,
      }
    })

  // Suggested batsmen for next batsman modal: ONLY from the batting team
  // Exclude players already batting or out in this innings
  const alreadyBattedNames = Object.keys(match.batsmen || {}).map((n) => n.toLowerCase())
  const battingTeamRoster = battingTeamRecord?.players?.length
    ? battingTeamRecord.players
    : savedPlayerNames
  const availableNextBatsmen = battingTeamRoster.filter(
    (p) => !alreadyBattedNames.includes(p.toLowerCase())
  )

  return (
    <div className="flex-1 flex flex-col justify-between min-h-screen bg-[#f5f6f8] relative">
      {/* Top Header Bar */}
      <header className="bg-[#2E7D32] text-white px-3 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate({ to: '/' })}
            className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            aria-label="Back"
          >
            <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <span className="text-lg font-bold tracking-tight truncate max-w-[190px]">
            {titleText}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isViewOnly && (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/90 text-white text-xs font-bold uppercase tracking-wider animate-pulse">
              <span className="size-2 rounded-full bg-white" /> Live
            </span>
          )}

          <button
            onClick={() =>
              navigate({
                to: '/match/$matchId/scorecard',
                params: { matchId: match.id },
                search: search.mode ? { mode: search.mode, token: search.token } : undefined,
              })
            }
            className="flex items-center gap-1 px-2.5 py-1 bg-black/20 hover:bg-black/30 rounded-lg text-white font-bold text-xs border border-white/30 transition-all active:scale-95 shadow-xs"
            title="Scorecard & Overs breakdown"
          >
            <span>1:3</span>
            <span className="text-[11px] font-medium opacity-90 hidden sm:inline">Scorecard</span>
          </button>

          {!isViewOnly && match.currentInnings === 1 && match.status !== 'completed' && (
            <button
              onClick={() => {
                setEditOversInput(match.totalOvers)
                setEditPlayersInput(match.settings.playersPerTeam)
                setEditError('')
                setShowEditSettingsModal(true)
              }}
              className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90"
              title="Edit Overs & Players Count (1st Innings)"
              aria-label="Edit Overs and Players Count"
            >
              <Edit2 className="size-5" />
            </button>
          )}

          {/* Offline Sync Button - only visible when offline or currently syncing */}
          {!isViewOnly && (!isOnline || isSyncing) && (
            <button
              onClick={handleSyncToOnline}
              disabled={isSyncing}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-amber-600/90 hover:bg-amber-600 text-white border-white/30 transition-all active:scale-95 shadow-xs"
              title="You are offline. Connect to internet and click to sync data online."
            >
              {isSyncing ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <CloudOff className="size-3.5 text-amber-200" />
              )}
              <span className="text-[11px] font-medium hidden sm:inline">
                {isSyncing ? 'Syncing...' : 'Sync to Online'}
              </span>
            </button>
          )}

          {!isViewOnly && (
            <button
              onClick={() => setShowShareModal(true)}
              className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90"
              title="Share match link"
            >
              <Share2 className="size-5.5" />
            </button>
          )}
        </div>
      </header>

      {/* Sync Status Banner */}
      {syncStatusMsg && (
        <div className="bg-emerald-900 text-white text-xs py-1.5 px-4 text-center font-medium animate-in fade-in transition-all">
          {syncStatusMsg}
        </div>
      )}

      {/* Match ID Bar with Quick Copy for friends/viewers */}
      <div className="bg-[#1f2937] text-gray-200 px-3.5 py-1.5 flex items-center justify-between text-xs border-b border-gray-700 shadow-2xs select-none">
        <span className="font-medium text-gray-300 flex items-center gap-1.5 truncate">
          <span className="opacity-80">Match ID:</span>
          <span className="font-mono text-white select-all font-semibold tracking-wide">{displayMatchId}</span>
        </span>
        <button
          type="button"
          onClick={copyMatchId}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 active:scale-95 text-white transition-all cursor-pointer font-medium ml-2 shrink-0"
          title="Copy Match ID to share with friends"
        >
          {copiedMatchId ? (
            <>
              <Check className="size-3.5 text-emerald-400" />
              <span className="text-[11px] text-emerald-300">Copied</span>
            </>
          ) : (
            <>
              <Copy className="size-3.5" />
              <span className="text-[11px]">Copy ID</span>
            </>
          )}
        </button>
      </div>

      {/* Main Console Content */}
      <div className="p-2.5 sm:p-3 space-y-2 sm:space-y-2.5 flex-1 overflow-x-hidden">
        {/* Score Header Card: 1st Inning (5.jpg) or 2nd Inning with Target & RR (18.jpg) */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 shadow-sm border border-gray-100 space-y-1 sm:space-y-1.5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs sm:text-sm font-semibold text-gray-800">
                {match.battingTeam}, {match.currentInnings === 1 ? '1st' : '2nd'} inning
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-0.5 tracking-tight">
                {match.totalRuns} - {match.totalWickets}{' '}
                <span className="text-lg sm:text-xl font-normal text-gray-600">
                  ({formatOvers(match.legalBallsDelivered)})
                </span>
              </div>
            </div>

            {/* Run Rate / Target Columns matching Screen 18 */}
            {!isSecondInning ? (
              <div className="text-right">
                <div className="text-xs font-semibold text-gray-700">CRR</div>
                <div className="text-sm sm:text-base font-bold text-gray-900 mt-0.5">{crr}</div>
              </div>
            ) : (
              <div className="flex items-center gap-4 sm:gap-6 text-right">
                <div>
                  <div className="text-[11px] sm:text-xs font-semibold text-gray-600">CRR</div>
                  <div className="text-xs sm:text-sm font-bold text-gray-900 mt-0.5">{crr}</div>
                </div>
                <div>
                  <div className="text-[11px] sm:text-xs font-semibold text-gray-600">Target</div>
                  <div className="text-xs sm:text-sm font-bold text-gray-900 mt-0.5">{target}</div>
                </div>
                <div>
                  <div className="text-[11px] sm:text-xs font-semibold text-gray-600">RR</div>
                  <div className="text-xs sm:text-sm font-bold text-gray-900 mt-0.5">{rrr}</div>
                </div>
              </div>
            )}
          </div>

          {/* 2nd Inning Target Chasing status line (Screen 18) */}
          {isSecondInning && (
            <div className="text-xs font-semibold text-[#2E7D32]">
              {match.battingTeam} need {remainingRuns} runs in {remainingBalls} balls
            </div>
          )}
        </div>

        {/* Batsmen & Bowler Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden text-xs sm:text-sm">
          <div className="grid grid-cols-12 px-3 py-1.5 bg-gray-50/70 text-gray-600 font-semibold text-[11px] sm:text-xs border-b border-gray-100">
            <div className="col-span-5">Batsman</div>
            <div className="col-span-1 text-center">R</div>
            <div className="col-span-1 text-center">B</div>
            <div className="col-span-1 text-center">4s</div>
            <div className="col-span-1 text-center">6s</div>
            <div className="col-span-3 text-right">SR</div>
          </div>

          <div className={`grid grid-cols-12 px-3 py-1.5 border-b border-gray-50 items-center transition-colors ${isRow1Striker ? 'bg-[var(--color-cricket-primary)]/10 font-bold' : ''}`}>
            <div className={`col-span-5 truncate flex items-center gap-1 ${isRow1Striker ? 'text-[var(--color-cricket-primary)] font-bold' : 'text-gray-800 dark:text-gray-200 font-medium'}`}>
              <span className="truncate">{batterRow1.name}</span>
              {isRow1Striker && <span className="inline-block text-xs" title="On strike">🏏</span>}
            </div>
            <div className="col-span-1 text-center font-bold text-gray-900 dark:text-white">{batterRow1.runs}</div>
            <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow1.balls}</div>
            <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow1.fours}</div>
            <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow1.sixes}</div>
            <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-mono text-[11px] sm:text-xs">
              {calculateStrikeRate(batterRow1.runs, batterRow1.balls)}
            </div>
          </div>

          {batterRow2 && (
            <div className={`grid grid-cols-12 px-3 py-1.5 border-b border-gray-100 items-center transition-colors ${isRow2Striker ? 'bg-[var(--color-cricket-primary)]/10 font-bold' : ''}`}>
              <div className={`col-span-5 truncate flex items-center gap-1 ${isRow2Striker ? 'text-[var(--color-cricket-primary)] font-bold' : 'text-gray-800 dark:text-gray-200 font-medium'}`}>
                <span className="truncate">{batterRow2.name}</span>
                {isRow2Striker && <span className="inline-block text-xs" title="On strike">🏏</span>}
              </div>
              <div className="col-span-1 text-center font-bold text-gray-900 dark:text-white">{batterRow2.runs}</div>
              <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow2.balls}</div>
              <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow2.fours}</div>
              <div className="col-span-1 text-center text-gray-600 dark:text-gray-400">{batterRow2.sixes}</div>
              <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-mono text-[11px] sm:text-xs">
                {calculateStrikeRate(batterRow2.runs, batterRow2.balls)}
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 px-3 py-1 bg-gray-50/70 text-gray-600 font-semibold text-[11px] sm:text-xs border-b border-gray-100">
            <div className="col-span-5">Bowler</div>
            <div className="col-span-1 text-center">O</div>
            <div className="col-span-1 text-center">M</div>
            <div className="col-span-1 text-center">R</div>
            <div className="col-span-1 text-center">W</div>
            <div className="col-span-3 text-right">ER</div>
          </div>

          <div className="grid grid-cols-12 px-3 py-1.5 items-center">
            <div className="col-span-5 font-semibold text-[#2E7D32] truncate">
              {currentBowlerStats.name}
            </div>
            <div className="col-span-1 text-center text-gray-700">{formatOvers(currentBowlerStats.legalBalls)}</div>
            <div className="col-span-1 text-center text-gray-700">{currentBowlerStats.maidens}</div>
            <div className="col-span-1 text-center text-gray-700">{currentBowlerStats.runsConceded}</div>
            <div className="col-span-1 text-center font-bold text-gray-900">{currentBowlerStats.wickets}</div>
            <div className="col-span-3 text-right text-gray-700 font-mono text-[11px] sm:text-xs">
              {calculateEconomyRate(currentBowlerStats.runsConceded, currentBowlerStats.legalBalls)}
            </div>
          </div>
        </div>

        {/* This Over Deliveries Bar - horizontally scrollable with no ball shrinking */}
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-100 flex items-center gap-2.5 min-h-[46px] overflow-hidden">
          <span className="text-xs font-bold text-gray-600 shrink-0">This over:</span>
          <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 scrollbar-none flex-1">
            {match.currentOverDeliveries.length === 0 ? (
              <span className="text-xs text-gray-400 italic">No balls yet in this over</span>
            ) : (
              match.currentOverDeliveries.map((del) => {
                const isFour = del.runsScored === 4 && del.extraType === 'none'
                const isSix = del.runsScored === 6 && del.extraType === 'none'
                const isWkt = del.isWicket
                return (
                  <span
                    key={del.id}
                    className={`size-7.5 shrink-0 rounded-full flex items-center justify-center text-xs font-bold shadow-xs border ${isWkt
                        ? 'bg-red-600 text-white border-red-700'
                        : isSix
                          ? 'bg-[#2E7D32] text-white border-[#1B5E20]'
                          : isFour
                            ? 'bg-[#E65100] text-white border-orange-600'
                            : 'bg-white text-gray-800 border-gray-300'
                      }`}
                  >
                    {del.displayBadge}
                  </span>
                )
              })
            )}
          </div>
        </div>

        {/* Extras & Action Checkboxes - only visible when not view only and inning in progress */}
        {!isViewOnly && !isMatchFinished && !isFirstInningOver && (
          <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm border border-gray-100 space-y-2">
            <div className="grid grid-cols-4 gap-1.5">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-800 select-none">
                <Checkbox
                  checked={selectedExtra === 'wide'}
                  onCheckedChange={(checked) => setSelectedExtra(checked ? 'wide' : 'none')}
                />
                Wide
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-800 select-none">
                <Checkbox
                  checked={selectedExtra === 'no_ball'}
                  onCheckedChange={(checked) => setSelectedExtra(checked ? 'no_ball' : 'none')}
                />
                No Ball
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-800 select-none">
                <Checkbox
                  checked={selectedExtra === 'bye'}
                  onCheckedChange={(checked) => setSelectedExtra(checked ? 'bye' : 'none')}
                />
                Byes
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-800 select-none">
                <Checkbox
                  checked={selectedExtra === 'leg_bye'}
                  onCheckedChange={(checked) => setSelectedExtra(checked ? 'leg_bye' : 'none')}
                />
                Leg Byes
              </label>
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm font-semibold text-gray-800 select-none">
                <Checkbox
                  checked={isWicketChecked}
                  onCheckedChange={(c) => setIsWicketChecked(!!c)}
                />
                Wicket
              </label>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPlayerToRetire(match.striker)
                    setShowRetireModal(true)
                  }}
                  className="bg-[#2E7D32] text-white text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#1B5E20] active:scale-95 transition-all"
                >
                  Retire
                </button>
                <button
                  type="button"
                  onClick={() => swapBatsmen()}
                  className="bg-[#2E7D32] text-white text-[11px] sm:text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-[#1B5E20] active:scale-95 transition-all"
                >
                  Swap Batsman
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scoring Area or Status Card */}
        {isViewOnly ? (
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100 space-y-2 mt-4">
            <p className="text-gray-700 font-medium">
              You are currently viewing this match in <strong className="text-[#2E7D32]">View-Only</strong> mode.
            </p>
            <p className="text-xs text-gray-400">
              Live updates are automatically synchronized in real-time.
            </p>
          </div>
        ) : isFirstInningOver ? (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-4 mt-2">
            <div className="text-center space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--color-cricket-primary)]">
                Innings Break
              </span>
              <h4 className="text-base font-extrabold text-gray-900">
                1st Innings Completed ({formatOvers(match.legalBallsDelivered)} ov)
              </h4>
              <p className="text-xs text-gray-600">
                {match.bowlingTeam} need <strong className="text-gray-900 font-bold">{match.totalRuns + 1}</strong> runs in {match.totalOvers} overs to win.
              </p>
            </div>

            <div className="flex items-center gap-2.5 pt-1">
              <button
                type="button"
                onClick={undoLastDelivery}
                className="flex-1 bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-800 font-semibold py-3 rounded-xl text-xs sm:text-sm border border-gray-200 transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-2xs"
                title="Undo last delivery of 1st innings if scored by mistake"
              >
                <span>↺</span>
                <span>Undo Last Ball</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const runsNeeded = match.totalRuns + 1
                  const reqRR = (runsNeeded / match.totalOvers).toFixed(2)
                  setEndOfInningsData({
                    chasingTeam: match.bowlingTeam,
                    runsNeeded,
                    overs: match.totalOvers,
                    rrr: reqRR,
                  })
                  setShowEndOfInningsModal(true)
                }}
                className="flex-1 theme-btn-primary font-semibold py-3 rounded-xl text-xs sm:text-sm shadow-xs cursor-pointer flex items-center justify-center gap-1.5 active:scale-95 transition-all"
              >
                <span>Start 2nd Inning</span>
                <span>→</span>
              </button>
            </div>
          </div>
        ) : isMatchFinished ? (
          <div className="bg-white rounded-xl p-5 text-center shadow-sm border border-gray-100 space-y-3 mt-3">
            <p className="text-gray-900 font-bold text-sm">Match Completed</p>
            <p className="text-xs font-semibold text-[var(--color-cricket-primary)]">
              {match.winnerText || 'Match has concluded.'}
            </p>
            <div className="pt-1">
              <button
                type="button"
                onClick={undoLastDelivery}
                className="bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-800 font-semibold px-4 py-2 rounded-xl text-xs border border-gray-200 transition-all cursor-pointer inline-flex items-center gap-1.5"
                title="Undo final ball"
              >
                <span>↺</span>
                <span>Undo Last Ball</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-2 pt-0.5">
            <div className="col-span-4 flex flex-col justify-between gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={undoLastDelivery}
                className="flex-1 bg-[#2E7D32] text-white font-semibold rounded-xl text-xs sm:text-sm flex items-center justify-center hover:bg-[#1B5E20] active:scale-95 transition-all shadow-xs min-h-[38px] sm:min-h-[44px]"
              >
                Undo
              </button>
              <button
                type="button"
                onClick={() => setShowPartnershipsSheet(true)}
                className="flex-1 bg-[#2E7D32] text-white font-semibold rounded-xl text-xs sm:text-sm flex items-center justify-center hover:bg-[#1B5E20] active:scale-95 transition-all shadow-xs min-h-[38px] sm:min-h-[44px]"
              >
                Partnerships
              </button>
              <button
                type="button"
                onClick={() => setShowExtrasSheet(true)}
                className="flex-1 bg-[#2E7D32] text-white font-semibold rounded-xl text-xs sm:text-sm flex items-center justify-center hover:bg-[#1B5E20] active:scale-95 transition-all shadow-xs min-h-[38px] sm:min-h-[44px]"
              >
                Extras
              </button>
            </div>

            <div className="col-span-8 grid grid-cols-4 gap-1.5 sm:gap-2 theme-card p-2.5 sm:p-3 rounded-2xl border border-[var(--theme-border)] shadow-sm">
              {[0, 1, 2, 3].map((val) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => handleScoreBall(val)}
                  className="size-11 sm:size-12 rounded-full border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-base font-bold flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 hover:border-[var(--color-cricket-primary)] hover:text-[var(--color-cricket-primary)] active:scale-90 transition-all shadow-xs mx-auto cursor-pointer"
                >
                  {val}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleScoreBall(4)}
                className="size-11 sm:size-12 rounded-full border border-[#E65100] text-[#E65100] text-base font-bold flex items-center justify-center hover:bg-orange-500/10 active:scale-90 transition-all shadow-xs mx-auto cursor-pointer"
              >
                4
              </button>
              <button
                type="button"
                onClick={() => handleScoreBall(5)}
                className="size-11 sm:size-12 rounded-full border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-base font-bold flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 hover:border-[var(--color-cricket-primary)] hover:text-[var(--color-cricket-primary)] active:scale-90 transition-all shadow-xs mx-auto cursor-pointer"
              >
                5
              </button>
              <button
                type="button"
                onClick={() => handleScoreBall(6)}
                className="size-11 sm:size-12 rounded-full border border-[var(--color-cricket-primary)] text-[var(--color-cricket-primary)] text-base font-bold flex items-center justify-center hover:bg-[var(--color-cricket-primary)]/10 active:scale-90 transition-all shadow-xs mx-auto cursor-pointer"
              >
                6
              </button>
              <button
                type="button"
                onClick={() => handleScoreBall(7)}
                className="size-11 sm:size-12 rounded-full border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-base font-bold flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 hover:border-[var(--color-cricket-primary)] hover:text-[var(--color-cricket-primary)] active:scale-90 transition-all shadow-xs mx-auto cursor-pointer"
              >
                7
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Screen 19: Choose Bowler Modal with Consistent Mobile Container Width */}
      {showBowlerModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-stretch animate-in fade-in">
          <div className="w-full max-w-md min-h-screen bg-[var(--theme-bg,#f5f6f8)] flex flex-col justify-between shadow-2xl relative">
            <div className="flex-1 flex flex-col">
              <header className="bg-[var(--color-cricket-primary)] text-white px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (match.overPendingBowler) {
                        setBowlerSelectError('Please choose the new bowler for this over before closing.')
                        return
                      }
                      setShowBowlerModal(false)
                    }}
                    className="p-1 hover:bg-white/10 rounded-full"
                  >
                    <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h2 className="text-lg font-bold">Choose bowler</h2>
                </div>
                {/* Quick score status on header */}
                <div className="text-right">
                  <div className="text-xs font-semibold text-white/90">
                    {match.totalRuns} - {match.totalWickets}
                  </div>
                  <div className="text-[10px] text-white/80">
                    ({formatOvers(match.legalBallsDelivered)} ov)
                  </div>
                </div>
              </header>

              {/* Current Match Live Score Banner */}
              <div className="theme-card border-b border-[var(--theme-border)] px-4 py-3 flex items-center justify-between shadow-2xs shrink-0">
                <div>
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                    {match.battingTeam} ({match.currentInnings === 1 ? '1st' : '2nd'} Inning)
                  </span>
                  <span className="text-xl font-extrabold text-gray-900 dark:text-white">
                    {match.totalRuns} / {match.totalWickets}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                    Overs Bowled
                  </span>
                  <span className="text-base font-bold text-[var(--color-cricket-primary)]">
                    {formatOvers(match.legalBallsDelivered)} / {match.totalOvers} ov
                  </span>
                </div>
              </div>

              {/* Body form with flex-1 and justify-between so Done is pinned to bottom */}
              <div className="flex-1 flex flex-col justify-between p-4">
                <div className="space-y-4">
                  {bowlerSelectError && (
                    <div className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-lg border border-red-200">
                      {bowlerSelectError}
                    </div>
                  )}

                  <label className="text-sm font-semibold text-[var(--color-cricket-primary)] block">
                    Select a new bowler
                  </label>

                  {/* Clean Autocomplete Select matching Team & Opener Select */}
                  <div>
                    <AutocompleteSelect
                      placeholder="Type or select player name"
                      value={nextBowlerName}
                      onChange={(val) => {
                        setNextBowlerName(val)
                        setBowlerSelectError('')
                      }}
                      options={suggestedBowlers}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="pt-4 pb-2">
                  <button
                    type="button"
                    onClick={() => handleBowlerChosen(nextBowlerName)}
                    className="w-full bg-[var(--color-cricket-primary)] text-white font-semibold py-3.5 rounded-lg shadow-sm hover:opacity-90 active:scale-95 transition-all text-base cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screen 20: Match Victory / Draw Celebration Screen */}
      {showCelebration && celebrationData && (
        <div className="fixed inset-0 bg-[#f5f6f8] z-50 flex flex-col justify-between overflow-hidden">
          {/* Top Bar */}
          <header className="bg-[#2E7D32] text-white px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => {
                setShowCelebration(false)
                navigate({ to: '/match/$matchId/scorecard', params: { matchId: match.id } })
              }}
              className="p-1 hover:bg-white/10 rounded-full"
            >
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-white/80">Match Finished</span>
          </header>

          {/* Falling Confetti Layer with dynamic colors and movement */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
            {Array.from({ length: celebrationData.isDraw ? 36 : 60 }).map((_, i) => {
              const colors = celebrationData.isDraw
                ? ['#9E9E9E', '#607D8B', '#00BCD4', '#8BC34A', '#FFC107']
                : ['#E91E63', '#9C27B0', '#FFEB3B', '#4CAF50', '#00E676', '#FF9800', '#2196F3']
              const color = colors[i % colors.length]
              const left = `${(i * 17) % 100}%`
              const top = `${(i * 13) % 95}%`
              const size = (i % 3) + 7
              const isCircle = i % 2 === 0
              return (
                <div
                  key={i}
                  className="absolute opacity-80 animate-pulse"
                  style={{
                    left,
                    top,
                    width: `${size}px`,
                    height: `${size * (isCircle ? 1 : 1.6)}px`,
                    backgroundColor: color,
                    borderRadius: isCircle ? '50%' : '2px',
                    transform: `rotate(${i * 25}deg)`,
                  }}
                />
              )
            })}
          </div>

          {/* Center Trophy / Handshake and Content */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-6 z-20">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              {celebrationData.isDraw ? 'Match Tied / Draw' : 'Congratulations'}
            </h1>

            {/* Circular Background with Trophy or Draw Emblem */}
            <div
              className={`size-48 rounded-full ${celebrationData.isDraw ? 'bg-[#546E7A]' : 'bg-[#7CB342]'
                } flex items-center justify-center shadow-xl relative p-6 transition-all transform hover:scale-105 duration-300`}
            >
              {!celebrationData.isDraw ? (
                <svg
                  viewBox="0 0 100 100"
                  className="w-32 h-32 drop-shadow-md"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Base */}
                  <rect x="35" y="76" width="30" height="12" rx="2" fill="#C62828" />
                  <rect x="40" y="80" width="20" height="4" rx="1" fill="#FFFFFF" />
                  <rect x="46" y="66" width="8" height="10" fill="#FBC02D" />
                  {/* Cup */}
                  <path
                    d="M28 26 H72 V48 C72 60 62 68 50 68 C38 68 28 60 28 48 Z"
                    fill="#FFEB3B"
                  />
                  <path
                    d="M32 30 H68 V48 C68 58 60 64 50 64 C40 64 32 58 32 48 Z"
                    fill="#FDD835"
                  />
                  {/* Handles */}
                  <path
                    d="M28 32 C18 32 18 46 28 48"
                    stroke="#FFD54F"
                    strokeWidth="5"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <path
                    d="M72 32 C82 32 82 46 72 48"
                    stroke="#FFD54F"
                    strokeWidth="5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              ) : (
                <div className="text-white text-center space-y-1">
                  <div className="text-5xl">🤝</div>
                  <div className="text-xs font-bold uppercase tracking-widest text-white/90">Equal Honors</div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {celebrationData.isDraw
                  ? `${match.hostTeam} & ${match.visitorTeam}`
                  : celebrationData?.winnerTeam || match.winnerTeam || match.battingTeam}
              </h2>
              <p className="text-sm font-medium text-gray-800">
                {celebrationData?.winnerText || match.winnerText || `${match.battingTeam} won the match.`}
              </p>
            </div>

            <div className="pt-4 flex gap-3">
              <button
                onClick={() => navigate({ to: '/match/$matchId/scorecard', params: { matchId: match.id } })}
                className="bg-[#2E7D32] text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm hover:bg-[#1B5E20] transition-colors text-sm"
              >
                View Scorecard
              </button>
              <button
                onClick={() => navigate({ to: '/history' })}
                className="bg-white border border-gray-300 text-gray-800 font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                Match History
              </button>
            </div>
          </div>

          <div className="h-6" />
        </div>
      )}

      {/* Screen 13: Select player to retire Modal */}
      {showRetireModal && (
        <div className="fixed inset-0 bg-[#f5f6f8] z-50 flex flex-col justify-between">
          <div>
            <header className="bg-[#2E7D32] text-white px-4 py-3 flex items-center gap-3">
              <button
                onClick={() => setShowRetireModal(false)}
                className="p-1 hover:bg-white/10 rounded-full"
              >
                <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h2 className="text-lg font-bold">
                {match.hostTeam} v/s {match.visitorTeam}
              </h2>
            </header>

            <form onSubmit={handleConfirmRetire} className="p-4 space-y-6">
              <div>
                <label className="text-sm font-semibold text-[#2E7D32] block mb-3">
                  Select player to retire
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="playerRetire"
                      checked={playerToRetire === match.striker}
                      onChange={() => setPlayerToRetire(match.striker)}
                      className="accent-[#2E7D32] size-5"
                    />
                    <span className="text-base text-gray-800">{match.striker}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="playerRetire"
                      checked={playerToRetire === match.nonStriker}
                      onChange={() => setPlayerToRetire(match.nonStriker)}
                      className="accent-[#2E7D32] size-5"
                    />
                    <span className="text-base text-gray-800">{match.nonStriker}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-[#2E7D32] block mb-2">
                  Replaced by?
                </label>
                <AutocompleteSelect
                  placeholder="Player name"
                  value={replacementName}
                  onChange={setReplacementName}
                  options={availableNextBatsmen}
                  autoFocus
                  required
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-[#2E7D32] text-white font-semibold py-3.5 rounded-lg shadow-sm hover:bg-[#1B5E20] text-base"
                >
                  Done
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Screen 14: End of the first inning Modal */}
      {showEndOfInningsModal && endOfInningsData && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 text-left">
            <h3 className="text-xl font-bold text-gray-900">End of the first inning</h3>
            <p className="text-base text-gray-800">
              {endOfInningsData.chasingTeam} need {endOfInningsData.runsNeeded} runs in {endOfInningsData.overs} overs.
            </p>
            <p className="text-base text-gray-800 font-medium">
              Required Run Rate: {endOfInningsData.rrr}
            </p>
            <div className="flex justify-end gap-6 pt-3">
              <button
                type="button"
                onClick={() => setShowEndOfInningsModal(false)}
                className="text-sm font-bold text-[#2E7D32] uppercase hover:underline"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEndOfInningsModal(false)
                  navigate({
                    to: '/match/openers',
                    search: { inning: 2 },
                  })
                }}
                className="text-sm font-bold text-[#2E7D32] uppercase hover:underline"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screen 9: Partnerships Bottom Sheet */}
      {/* Screen 9: Partnerships Bottom Sheet (exact match to 9.jpg) */}
      {showPartnershipsSheet && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
          onClick={() => setShowPartnershipsSheet(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-md p-5 space-y-4 shadow-2xl animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const p1Name = match.currentPartnership.player1Name || match.striker
              const p2Name = match.currentPartnership.player2Name || match.nonStriker
              const p1Runs = match.currentPartnership.player1Runs || 0
              const p2Runs = match.currentPartnership.player2Runs || 0
              const totalPartnershipRuns = match.currentPartnership.runs || 0
              const isSingle = Boolean(match.settings.singlePlayerMode || match.settings.playersPerTeam === 1 || !p2Name)

              // Percentage width based on each player's contribution
              const p1Percent = totalPartnershipRuns > 0 ? Math.round((p1Runs / totalPartnershipRuns) * 100) : 50
              const p2Percent = totalPartnershipRuns > 0 ? Math.round((p2Runs / totalPartnershipRuns) * 100) : 50

              return (
                <>
                  <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
                    <span>{p1Name}</span>
                    <span>{isSingle ? '' : p2Name}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Left Player Bar */}
                    <div className="flex-1 bg-gray-200 h-6 rounded-sm overflow-hidden flex justify-end">
                      <div
                        className="bg-[#2E7D32] h-full text-xs text-white font-bold flex items-center justify-center px-2 transition-all min-w-[24px]"
                        style={{ width: `${isSingle ? 100 : Math.max(15, p1Percent)}%` }}
                      >
                        {p1Runs}
                      </div>
                    </div>

                    {/* Center Partnership Total Runs & Balls */}
                    <div className="text-center min-w-[56px] px-1">
                      <div className="text-lg font-extrabold text-gray-900 leading-none">
                        {totalPartnershipRuns}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        ({match.currentPartnership.balls})
                      </div>
                    </div>

                    {/* Right Player Bar (if 2 players) */}
                    {!isSingle && (
                      <div className="flex-1 bg-gray-200 h-6 rounded-sm overflow-hidden">
                        <div
                          className="bg-[#2E7D32] h-full text-xs text-white font-bold flex items-center justify-center px-2 transition-all min-w-[24px]"
                          style={{ width: `${Math.max(15, p2Percent)}%` }}
                        >
                          {p2Runs}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 font-medium">
                    Extras: {match.extras.byes + match.extras.legByes + match.extras.wides + match.extras.noBalls}
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Screen 10: Extras Bottom Sheet */}
      {showExtrasSheet && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
          onClick={() => setShowExtrasSheet(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-md p-5 shadow-2xl animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-medium text-gray-900">
              Extras: {match.extras.byes} B, {match.extras.legByes} LB, {match.extras.wides} WD, {match.extras.noBalls} NB, {match.extras.penalty} P
            </div>
          </div>
        </div>
      )}

      {/* Custom Runs Modal */}
      {showCustomRunsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-xs w-full p-5 space-y-4">
            <h4 className="font-bold text-gray-900 text-lg">Custom Runs</h4>
            <input
              type="number"
              min="0"
              max="20"
              value={customRunsInput}
              onChange={(e) => setCustomRunsInput(Number(e.target.value))}
              className="w-full border-b border-gray-400 py-1 text-center text-xl font-bold"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCustomRunsModal(false)}
                className="px-3 py-1.5 text-sm text-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCustomRunsModal(false)
                  handleScoreBall(customRunsInput)
                }}
                className="px-4 py-1.5 text-sm bg-[#2E7D32] text-white rounded font-semibold"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wicket Modal */}
      {showWicketModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-red-600">Wicket Fall</h3>
            <div className="space-y-3">
              {/* Dismissed Batsman Selection - only needed if 2 batsmen are on the pitch */}
              {Boolean(match.nonStriker && !match.settings.singlePlayerMode && match.settings.playersPerTeam > 1) ? (
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Dismissed Batsman</label>
                  <div className="flex gap-4">
                    <label className="text-sm flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="outBatter"
                        checked={dismissedBatsman === match.striker}
                        onChange={() => setDismissedBatsman(match.striker)}
                        className="accent-[#2E7D32]"
                      />
                      {match.striker} (Striker)
                    </label>
                    <label className="text-sm flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="outBatter"
                        checked={dismissedBatsman === match.nonStriker}
                        onChange={() => setDismissedBatsman(match.nonStriker)}
                        className="accent-[#2E7D32]"
                      />
                      {match.nonStriker}
                    </label>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-600 font-medium">
                  Dismissed: <strong className="text-gray-900">{match.striker}</strong>
                </div>
              )}

              {/* Next Batsman Name - only asked if team is NOT all out on this wicket */}
              {(() => {
                const maxAllowedWickets = Math.max(1, match.settings.playersPerTeam - 1)
                const isLastWicket = match.totalWickets + 1 >= maxAllowedWickets
                if (isLastWicket) {
                  return (
                    <div className="bg-amber-50 text-amber-800 text-xs px-3 py-2 rounded-lg border border-amber-200 font-medium">
                      This is the final wicket for {match.battingTeam}. The innings will close.
                    </div>
                  )
                }

                return (
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1.5">Next Batsman Name</label>
                    <AutocompleteSelect
                      placeholder="Enter batsman name"
                      value={newBatsmanInput}
                      onChange={setNewBatsmanInput}
                      options={availableNextBatsmen}
                      required
                    />
                  </div>
                )
              })()}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowWicketModal(false)
                  setIsWicketChecked(false)
                }}
                className="px-3 py-1.5 text-sm text-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmWicket(0)}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-semibold hover:bg-red-700"
              >
                Confirm Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First Inning Match Settings Edit Modal (Overs & Players Count) */}
      {showEditSettingsModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowEditSettingsModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Edit Match</h3>
                <p className="text-xs text-gray-500">1st Innings Settings</p>
              </div>
              <button
                onClick={() => setShowEditSettingsModal(false)}
                className="text-gray-400 hover:text-gray-700 font-bold text-lg p-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {editError && (
              <div className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-lg border border-red-200">
                {editError}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault()
                const newOvers = Number(editOversInput)
                const newPlayers = Number(editPlayersInput)

                if (!newOvers || newOvers < 1) {
                  setEditError('Overs must be at least 1')
                  return
                }

                const currentCompletedBalls = match.legalBallsDelivered
                const minOversRequired = Math.ceil(currentCompletedBalls / 6)
                if (newOvers < minOversRequired) {
                  setEditError(
                    `Cannot set overs lower than overs already bowled (${minOversRequired} overs bowled)`,
                  )
                  return
                }

                if (!newPlayers || newPlayers < 1) {
                  setEditError('Players per team must be at least 1')
                  return
                }

                // In cricket, total wickets allowed before all-out is playersPerTeam - 1 (or 1 for single player)
                const currentWickets = match.totalWickets
                if (newPlayers > 1 && currentWickets >= newPlayers) {
                  setEditError(
                    `Players per team must be greater than current wickets lost (${currentWickets})`,
                  )
                  return
                }

                const res = updateMatchSettingsFirstInnings({
                  totalOvers: newOvers,
                  playersPerTeam: newPlayers,
                })
                setShowEditSettingsModal(false)

                // If overs count reduction completed the 1st innings right away, trigger end of innings modal
                if (res?.inningsCompleted) {
                  setEndOfInningsData({
                    chasingTeam: res.chasingTeam,
                    runsNeeded: res.runsNeeded,
                    overs: res.overs,
                    rrr: res.rrr,
                  })
                  setShowEndOfInningsModal(true)
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Overs
                </label>
                <input
                  type="number"
                  min={Math.max(1, Math.ceil(match.legalBallsDelivered / 6))}
                  max={100}
                  value={editOversInput}
                  onChange={(e) => {
                    setEditOversInput(e.target.value)
                    setEditError('')
                  }}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32] bg-white font-medium"
                  placeholder="e.g. 20"
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Current balls bowled: {match.legalBallsDelivered} ({formatOvers(match.legalBallsDelivered)} overs)
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Players per Team
                </label>
                <input
                  type="number"
                  min={Math.max(1, match.totalWickets + 1)}
                  max={50}
                  value={editPlayersInput}
                  onChange={(e) => {
                    setEditPlayersInput(e.target.value)
                    setEditError('')
                  }}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32] bg-white font-medium"
                  placeholder="e.g. 11"
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Current wickets lost: {match.totalWickets}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditSettingsModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 rounded-xl bg-[#2E7D32] text-white text-xs font-bold hover:bg-[#1B5E20] transition-colors shadow-sm active:scale-95"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role-Based Match Sharing Dialog */}
      {showShareModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Share Live Match</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-700 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="border border-gray-200 rounded-xl p-3.5 space-y-2 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                  👁️ View-Only Link (Viewers)
                </span>
                <span className="text-[10px] text-green-700 bg-green-100 font-bold px-1.5 py-0.5 rounded">
                  Read Only
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Fans and spectators can watch live score, over breakdowns & commentary. Keypad controls are hidden.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard('view')}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-gray-300 py-2 rounded-lg text-xs font-semibold text-gray-800 hover:bg-gray-100 active:scale-95 transition-all"
                >
                  {copiedLink === 'view' ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
                  {copiedLink === 'view' ? 'Copied Link!' : 'Copy Link'}
                </button>
                <button
                  onClick={() => shareToWhatsApp('view')}
                  className="flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-3 py-2 rounded-lg text-xs font-semibold active:scale-95 transition-all shadow-xs"
                  title="Share View Link to WhatsApp"
                >
                  <svg className="size-4 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>WhatsApp</span>
                </button>
              </div>
            </div>

            <div className="border border-[#2E7D32]/30 rounded-xl p-3.5 space-y-2 bg-green-50/40">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#1B5E20] uppercase tracking-wide">
                  ✏️ Co-Scorer / Editor Link
                </span>
                <span className="text-[10px] text-emerald-800 bg-emerald-200 font-bold px-1.5 py-0.5 rounded">
                  Full Access
                </span>
              </div>
              <p className="text-xs text-gray-600">
                Allows editing, score input, wicket registration and undo capabilities.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard('edit')}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#2E7D32] text-white py-2 rounded-lg text-xs font-semibold hover:bg-[#1B5E20] active:scale-95 transition-all shadow-xs"
                >
                  {copiedLink === 'edit' ? <Check className="size-4" /> : <Copy className="size-4" />}
                  {copiedLink === 'edit' ? 'Copied Scorer Link!' : 'Copy Scorer Link'}
                </button>
                <button
                  onClick={() => shareToWhatsApp('edit')}
                  className="flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-3 py-2 rounded-lg text-xs font-semibold active:scale-95 transition-all shadow-xs"
                  title="Share Scorer Link to WhatsApp"
                >
                  <svg className="size-4 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animated 4 & 6 Scope Zoom: ONLY for viewers (view-only spectators), not scorer */}
      {isViewOnly && boundaryAnimation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <div className="animate-scope-number flex items-center justify-center">
            <span
              className="text-[190px] sm:text-[240px] font-black leading-none text-[#FBC02D]"
              style={{
                textShadow:
                  '0 3px 0 #F57F17, 0 6px 0 #E65100, 0 10px 25px rgba(0, 0, 0, 0.45), 0 0 45px rgba(251, 192, 45, 0.6)',
              }}
            >
              {boundaryAnimation}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
